import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from './components/Login';
// import Register from './components/Register';
import DashboardLayout from './components/Layout/DashboardLayout';
import Footer from './components/Footer/Footer';
import AuthService from './services/auth.service';

// Lazy load components
const Dashboard = lazy(() => import('./components/Dashboard/Dashboard'));
const Customers = lazy(() => import('./components/Customers/Customers'));
const Sales = lazy(() => import('./components/Sales/Sales'));
const Products = lazy(() => import('./components/Products/Products'));
const POSSales = lazy(() => import('./components/POSSales/POSSales'));
const Category = lazy(() => import('./components/Category/Category'));
const POSDashboard = lazy(() => import('./components/POSSales/POSDashboard'));
const RetailSales = lazy(() => import('./components/RetailSales/RetailSales'));
const Orders = lazy(() => import('./components/Orders/Orders'));
const Inventory = lazy(() => import('./components/Inventory/Inventory'));
const Settings = lazy(() => import('./components/Settings/Settings'));
const Reports = lazy(() => import('./components/Reports/Reports'));
const Payments = lazy(() => import('./components/Reports/Payments'));
const AdvanceReport = lazy(() => import('./components/Reports/AdvanceReport'));
const Profile = lazy(() => import('./components/Profile'));
const FacebookCatalog = lazy(() => import('./components/FacebookCatalog/FacebookCatalog'));
const WhatsAppChat = lazy(() => import('./components/WhatsAppChat/WhatsAppChat'));
const PrivacyPolicy = lazy(() => import('./components/Footer/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./components/Footer/TermsOfUse'));
const TermsConditions = lazy(() => import('./components/Footer/TermsConditions'));
const ThemePreview = lazy(() => import('./components/ThemePreview'));

const PrivateRoute = ({ children }) => {
  const user = AuthService.getCurrentUser();
  return user ? <DashboardLayout>{children}</DashboardLayout> : <Navigate to="/login" />;
};

const POSPrivateRoute = ({ children }) => {
  const user = AuthService.getCurrentUser();
  return user ? children : <Navigate to="/login" />;
};

// Loading component for Suspense fallback
const LoadingSpinner = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="flex flex-col items-center space-y-4">
      {/* <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      <p className="text-gray-600 font-medium">Loading...</p> */}
    </div>
  </div>
);


// Conditional Footer component
const ConditionalFooter = () => {
  const location = useLocation();
  
  // Pages where footer should be hidden
  const hideFooterPages = ['/privacy-policy', '/terms-of-use', '/terms-conditions'];
  
  // Don't show footer on these pages
  if (hideFooterPages.includes(location.pathname)) {
    return null;
  }
  
  return <Footer />;
};

function App() {
  const user = AuthService.getCurrentUser();
  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <Router>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
          <Route path="/login" element={<Login />} />
          {/* <Route path="/register" element={<Register />} /> */}
          
          {/* Footer Pages - Public Routes */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />

          {/* Theme Preview - Public Route for testing brand colors */}
          <Route path="/theme-preview" element={<ThemePreview />} />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <PrivateRoute>
                <Customers />
              </PrivateRoute>
            }
          />
          {/* <Route
            path="/foods"
            element={
              <PrivateRoute>
                <MilkTypes />
              </PrivateRoute>
            }
          /> */}
          <Route
            path="/sales"
            element={
              <PrivateRoute>
                <Sales />
              </PrivateRoute>
            }
          />
          <Route
            path="/retail-sales"
            element={
              <PrivateRoute>
                <RetailSales />
              </PrivateRoute>
            }
          />
          <Route
            path="/products"
            element={
              <PrivateRoute>
                <Products />
              </PrivateRoute>
            }
          />
          <Route
            path="/pos-sales"
            element={
              <PrivateRoute>
                <POSSales />
              </PrivateRoute>
            }
          />
          <Route
            path="/pos-dashboard"
            element={
              <POSPrivateRoute>
                <POSDashboard />
              </POSPrivateRoute>
            }
          />
          <Route
            path="/categories"
            element={
              <PrivateRoute>
                <Category />
              </PrivateRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <PrivateRoute>
                <Orders />
              </PrivateRoute>
            }
          />
          <Route
            path="/inventory"
            element={
              <PrivateRoute>
                <Inventory />
              </PrivateRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <PrivateRoute>
                <Reports />
              </PrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <Settings />
              </PrivateRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <PrivateRoute>
                <Payments />
              </PrivateRoute>
            }
          />
          <Route
            path="/advance-report"
            element={
              <PrivateRoute>
                <AdvanceReport />
              </PrivateRoute>
            }
          />
          <Route
            path="/facebook-catalog"
            element={
              <PrivateRoute>
                <FacebookCatalog />
              </PrivateRoute>
            }
          />
          <Route
            path="/whatsapp-chat"
            element={
              <PrivateRoute>
                <WhatsAppChat />
              </PrivateRoute>
            }
          />
          <Route path="/" element={<Navigate to="/login" />} />
        </Routes>
        </Suspense>
        {/* <ConditionalFooter /> */}
      </Router>
    </div>
  );
}

export default App;
